import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Users, ChevronRight, Trophy, Frown, Crown, QrCode, Copy, PartyPopper, Loader2, Volume2, VolumeX, BarChart3 } from "lucide-react";
import { useMaestroSfx } from "@/lib/maestroSfx";

const APP_URL = (typeof window !== "undefined") ? window.location.origin : "";

export default function MaestroHost() {
  const { code } = useParams();
  const nav = useNavigate();
  const hostToken = localStorage.getItem(`maestro_host_${code}`);
  const [room, setRoom] = useState(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const sfx = useMaestroSfx();
  const prevStatus = useRef(null);
  const prevIdx = useRef(-1);
  const prevPlayerCount = useRef(0);
  const lastTick = useRef(-1);
  const finalPlayed = useRef(false);

  // Polling every 1s
  useEffect(() => {
    if (!hostToken) { nav("/play/maestro"); return; }
    let alive = true;
    const tick = async () => {
      try { const r = await api.get(`/maestro/rooms/${code}`, { params: { host_token: hostToken } }); if (alive) setRoom(r.data); }
      catch (e) { if (alive && e.response?.status === 404) toast.error("Salle expirée"); }
    };
    tick();
    const iv = setInterval(tick, 1200);
    const clock = setInterval(() => setNow(Date.now()), 200);
    return () => { alive = false; clearInterval(iv); clearInterval(clock); };
  }, [code, hostToken, nav]);

  // Sound effects triggered on state transitions.
  useEffect(() => {
    if (!room) return;
    const status = room.status;
    const idx = room.current_idx;
    const playerCount = room.players?.length || 0;

    if (status === "lobby" && playerCount > prevPlayerCount.current) sfx.play("join");
    if (prevStatus.current !== null) {
      // Entering "playing" on a new question index → question start
      if (status === "playing" && (prevStatus.current !== "playing" || idx !== prevIdx.current)) {
        sfx.play("questionStart");
      }
      // Playing → revealing
      if (prevStatus.current === "playing" && status === "revealing") sfx.play("reveal");
      // Reveal or later → ended
      if (status === "ended" && !finalPlayed.current) { sfx.play("final"); finalPlayed.current = true; }
    }
    prevStatus.current = status;
    prevIdx.current = idx;
    prevPlayerCount.current = playerCount;
  }, [room, sfx]);

  const advance = async () => {
    setBusy(true);
    try { await api.post("/maestro/rooms/next", { code, host_token: hostToken }); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const joinLink = `${APP_URL}/play/join?code=${code}&maestro=1`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(joinLink)}`;

  if (!room) return <div className="p-10 text-center text-[#8B95A1]"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  // ─── Lobby ───
  if (room.status === "lobby") {
    return (
      <div className="max-w-4xl mx-auto p-5 md:p-10" data-testid="maestro-host-lobby">
        <Toaster theme="light" />
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl text-[#1F3A5F]">{room.title}</h1>
            <p className="text-[#4A5568] mt-1">Partagez le QR ou le code aux joueurs.</p>
          </div>
          <MuteToggle sfx={sfx} />
        </div>
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-white border border-[#1F3A5F]/10 p-6 text-center">
            <img src={qrUrl} alt="QR" className="w-64 h-64 mx-auto rounded-lg" />
            <div className="mt-3 font-mono text-3xl tracking-[0.4em] text-[#1F3A5F]">{code}</div>
            <button onClick={() => { navigator.clipboard.writeText(joinLink); toast.success("Lien copié"); }}
              className="mt-2 inline-flex items-center gap-1 text-xs text-[#E5276A] font-bold hover:underline">
              <Copy className="w-3 h-3" /> Copier le lien
            </button>
          </div>
          <div className="rounded-2xl bg-white border border-[#1F3A5F]/10 p-5">
            <div className="inline-flex items-center gap-2 text-[#E5276A]"><Users className="w-4 h-4" /><span className="font-bold text-sm">{room.players?.length || 0} joueurs</span></div>
            <div className="mt-3 space-y-2 max-h-80 overflow-auto">
              {(room.players || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between p-2 rounded bg-[#FBF7F3] text-sm">
                  <span className="font-bold text-[#1F3A5F]">{p.name}</span>
                  {p.team && <span className="text-[10px] px-2 py-0.5 rounded-full text-white font-bold" style={{ background: (room.teams || []).find(t => t.name === p.team)?.color || "#8B95A1" }}>{p.team}</span>}
                </div>
              ))}
              {(!room.players || room.players.length === 0) && <div className="text-xs text-[#8B95A1] italic text-center py-4">En attente des joueurs…</div>}
            </div>
          </div>
        </div>
        <button onClick={advance} disabled={busy || !room.players?.length} data-testid="maestro-host-start"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-[#E5276A] text-white font-bold text-lg hover:bg-[#C01661] disabled:opacity-50">
          Démarrer <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    );
  }

  // ─── Ended ───
  if (room.status === "ended") {
    const f = room.final || {};
    return (
      <div className="max-w-4xl mx-auto p-5 md:p-10" data-testid="maestro-host-ended">
        <Toaster theme="light" />
        <div className="flex justify-end mb-2"><MuteToggle sfx={sfx} /></div>
        <div className="text-center">
          <PartyPopper className="w-12 h-12 text-[#E5276A] mx-auto" />
          <h1 className="font-display text-4xl text-[#1F3A5F] mt-3">Classement final</h1>
        </div>
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <Podium title="🥇 Meilleur joueur" who={f.best_player} muted={false} />
          <Podium title="🥇 Meilleure équipe" team={f.best_team} muted={false} />
          <Podium title="🥲 Moins bon joueur" who={f.worst_player} muted subtitle="Il/elle offre la tournée ? 🍷" />
          <Podium title="🥲 Moins bonne équipe" team={f.worst_team} muted subtitle="Rendez-vous pour la revanche !" />
        </div>
        <div className="mt-8 rounded-2xl bg-white border border-[#1F3A5F]/10 p-5">
          <h3 className="font-display text-lg text-[#1F3A5F] mb-3">Classement complet</h3>
          <div className="space-y-1">
            {(f.players_sorted || []).map((p, i) => (
              <div key={p.id} className="flex items-center justify-between p-2 rounded bg-[#FBF7F3] text-sm">
                <span className="tabular-nums text-xs text-[#8B95A1] w-6">#{i + 1}</span>
                <span className="font-bold text-[#1F3A5F] flex-1 ml-2 truncate">{p.name}</span>
                {p.team && <span className="text-[10px] px-2 py-0.5 rounded-full text-white font-bold" style={{ background: (room.teams || []).find(t => t.name === p.team)?.color || "#8B95A1" }}>{p.team}</span>}
                <span className="ml-2 font-display text-[#E5276A] tabular-nums">{p.score}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-6 flex flex-col md:flex-row gap-3">
          <button onClick={() => nav(`/play/maestro/recap/${code}`)} data-testid="maestro-host-recap"
            className="flex-1 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] inline-flex items-center justify-center gap-2">
            <BarChart3 className="w-4 h-4" /> Voir les statistiques
          </button>
          <button onClick={() => nav("/play/maestro")} className="flex-1 py-3 rounded-xl bg-[#1F3A5F] text-white font-bold hover:bg-[#2A4870]">Retour aux quiz</button>
        </div>
      </div>
    );
  }

  // ─── Playing / Revealing ───
  const q = room.questions[room.current_idx];
  const startedAt = room.question_started_at ? new Date(room.question_started_at).getTime() : now;
  const elapsed = (now - startedAt) / 1000;
  const timeLeft = Math.max(0, (q.timer_s || 30) - elapsed);
  const pct = Math.max(0, Math.min(100, (timeLeft / (q.timer_s || 30)) * 100));

  // Emit timer ticks on integer second changes while playing.
  if (room.status === "playing") {
    const secLeft = Math.ceil(timeLeft);
    if (secLeft !== lastTick.current && secLeft > 0 && secLeft <= 5) {
      lastTick.current = secLeft;
      sfx.play(secLeft <= 3 ? "timerLast" : "timerTick");
    } else if (secLeft > 5) {
      lastTick.current = secLeft;
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-5 md:p-10" data-testid="maestro-host-play">
      <Toaster theme="light" />
      <div className="flex items-center justify-between text-xs text-[#8B95A1] font-bold uppercase tracking-widest">
        <span>Question {room.current_idx + 1}/{room.n_questions}</span>
        <div className="flex items-center gap-3">
          <span>{room.players?.length || 0} joueurs · code {code}</span>
          <MuteToggle sfx={sfx} compact />
        </div>
      </div>
      <h1 className="font-display text-2xl md:text-3xl text-[#1F3A5F] mt-3">{q.question}</h1>
      {room.status === "playing" && (
        <div className="mt-4 h-3 rounded-full bg-[#1F3A5F]/10 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#E5276A] to-[#F9C548] transition-[width] duration-200" style={{ width: `${pct}%` }} data-testid="maestro-host-timer" />
        </div>
      )}
      <div className="mt-6 grid grid-cols-2 gap-3">
        {q.choices.map((c, i) => {
          const isCorrect = room.status === "revealing" && q.correct_idx === i;
          const tally = q.tallies?.[i] || 0;
          return (
            <div key={i} className={`rounded-xl p-4 border-2 transition-colors ${isCorrect ? "bg-green-50 border-[#4CAF50]" : "bg-white border-[#1F3A5F]/10"}`}>
              <div className="font-display text-lg text-[#1F3A5F]">{c}</div>
              {room.status === "revealing" && <div className="text-xs text-[#4A5568] mt-1">{tally} réponse{tally > 1 ? "s" : ""}</div>}
            </div>
          );
        })}
      </div>
      {room.status === "revealing" && (
        <div className="mt-6 rounded-2xl bg-white border border-[#1F3A5F]/10 p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-[#E5276A] mb-2">Top 5</div>
          <div className="space-y-1">
            {(room.players_sorted || []).slice(0, 5).map((p, i) => (
              <div key={p.id} className="flex items-center justify-between text-sm">
                <span className="text-[#1F3A5F]"><b>#{i + 1}</b> {p.name}</span>
                <span className="font-display text-[#E5276A]">{p.score}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <button onClick={advance} disabled={busy} data-testid="maestro-host-next"
        className="mt-6 w-full py-3 rounded-2xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
        {room.status === "playing" ? "Révéler la réponse" : (room.current_idx + 1 < room.n_questions ? "Question suivante" : "Voir le classement final")}
        <ChevronRight className="w-4 h-4 inline ml-1" />
      </button>
    </div>
  );
}

function Podium({ title, who, team, muted, subtitle }) {
  const Icon = muted ? Frown : Crown;
  const data = who || team;
  if (!data) return null;
  const color = team ? team.color : "#E5276A";
  return (
    <div className={`rounded-2xl p-5 text-center ${muted ? "bg-[#FBF7F3] border border-[#1F3A5F]/10" : "bg-white border-2 border-[#F9C548]"}`}>
      <Icon className={`w-8 h-8 mx-auto ${muted ? "text-[#8B95A1]" : "text-[#F9C548]"}`} />
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#8B95A1] mt-2">{title}</div>
      <div className="font-display text-2xl mt-1" style={{ color: muted ? "#4A5568" : color }}>{data.name}</div>
      <div className="text-3xl font-display mt-1" style={{ color }}>{data.score} pts</div>
      {subtitle && <div className="text-[11px] italic text-[#8B95A1] mt-1">{subtitle}</div>}
    </div>
  );
}

function MuteToggle({ sfx, compact = false }) {
  const Icon = sfx.muted ? VolumeX : Volume2;
  return (
    <button onClick={sfx.toggleMute} data-testid="maestro-host-mute"
      title={sfx.muted ? "Réactiver le son" : "Couper le son"}
      className={`inline-flex items-center gap-1 rounded-full border transition-colors ${sfx.muted ? "border-[#1F3A5F]/15 text-[#8B95A1] bg-white" : "border-[#E5276A]/30 text-[#E5276A] bg-white hover:bg-[#FCE4ED]"} ${compact ? "px-2 py-1 text-[10px]" : "px-3 py-1.5 text-xs font-bold"}`}>
      <Icon className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
      {!compact && <span>{sfx.muted ? "Son coupé" : "Son activé"}</span>}
    </button>
  );
}
